// [BD feat] Images — bd-images.js
// Developer request: lazy images below the fold arrived late and popped in after a fast scroll / anchor jump.
//  1. Load earlier: once the page has loaded (idle time), the lazy BD images are promoted to eager a few at a
//     time in document order, so they download in the background while the user reads the hero.
//  2. Anchor jumps (bd-header.js → `bd:anchor` event): the target section's images — and those between the
//     viewport and the target — are promoted at once; the target's main image gets fetchpriority="high".
//  3. Fade instead of pop: an image that is not complete yet gets data-bd-fade (opacity 0, general.css) and
//     fades in on load (TODO(motion-capture): 400ms placeholder). Images already complete at init are never
//     touched, so nothing that is on screen blinks; reduced motion = no transition (CSS); no JS = no attribute.
//  The LCP/teaser image is eager (never lazy) and therefore never selected here — performance.md.
(function () {
  'use strict';

  var BATCH = 3; // images promoted per idle slice
  var BATCH_TIMEOUT = 1500; // ms to wait for a batch before the next one starts anyway

  function lazyImages() {
    return [].slice.call(document.querySelectorAll('img[loading="lazy"]')).filter(function (img) {
      return img.closest('[class*="bd-"]');
    });
  }

  function isComplete(img) {
    return img.complete && img.naturalWidth > 0;
  }

  function isRendered(img) {
    // hidden copy variants (mobile/desktop tiles, the hidden success section) must not be fetched
    return img.getClientRects().length > 0;
  }

  // --- 3. fade ---
  function fade(img) {
    if (img.dataset.bdFadeBound) return;
    img.dataset.bdFadeBound = '1';
    if (isComplete(img)) return; // already painted: show as is, no animation
    img.setAttribute('data-bd-fade', '');
    var show = function () { img.setAttribute('data-bd-fade', 'in'); };
    img.addEventListener('load', show, { once: true });
    img.addEventListener('error', function () { img.removeAttribute('data-bd-fade'); }, { once: true });
    if (isComplete(img)) show(); // completed between the check and the listener
  }

  // --- 1. + 2. promotion ---
  function promote(img, high) {
    if (img.loading !== 'lazy') return false;
    if (high) img.setAttribute('fetchpriority', 'high');
    img.setAttribute('loading', 'eager'); // switching to eager starts the fetch
    return true;
  }

  function idle(fn) {
    if ('requestIdleCallback' in window) window.requestIdleCallback(fn, { timeout: 1000 });
    else window.setTimeout(fn, 200);
  }

  function nextBatch() {
    var pending = lazyImages().filter(isRendered).slice(0, BATCH);
    if (!pending.length) return;
    var left = pending.length;
    var done = false;
    var proceed = function () {
      if (done) return;
      done = true;
      idle(nextBatch);
    };
    var one = function () { if (--left <= 0) proceed(); };
    pending.forEach(function (img) {
      img.addEventListener('load', one, { once: true });
      img.addEventListener('error', one, { once: true });
      promote(img, false);
      if (isComplete(img)) one();
    });
    window.setTimeout(proceed, BATCH_TIMEOUT);
  }

  function startBackgroundLoading() {
    if (document.readyState === 'complete') idle(nextBatch);
    else window.addEventListener('load', function () { idle(nextBatch); }, { once: true });
  }

  // Anchor jump: everything between the viewport and the target, target first (its main image high priority)
  document.addEventListener('bd:anchor', function (e) {
    var target = e.detail && e.detail.target;
    if (!target) return;
    var targetImgs = [].slice.call(target.querySelectorAll('img[loading="lazy"]')).filter(isRendered);
    if (targetImgs.length) promote(targetImgs[0], true);
    targetImgs.slice(1).forEach(function (img) { promote(img, false); });
    var top = Math.min(0, target.getBoundingClientRect().top);
    var bottom = Math.max(window.innerHeight, target.getBoundingClientRect().bottom);
    lazyImages().filter(isRendered).forEach(function (img) {
      var r = img.getBoundingClientRect();
      if (r.bottom >= top && r.top <= bottom) promote(img, false);
    });
  });

  function init() {
    lazyImages().forEach(fade);
  }

  init();
  startBackgroundLoading();

  // Shopify theme editor: sections re-render without a page load.
  document.addEventListener('shopify:section:load', function () {
    init();
    idle(nextBatch);
  });
})();
// [BD feat] Images
